import React, { memo, useRef } from 'react';
import CategoryButton from './CategoryButton';
import ImageButton from './ImageButton';
import ImageUploadButton from './ImageUploadButton';
import { FlattedCategory } from '@/types/category';


interface MediaListComponentProps {
    mediaList: FlattedCategory[];
    handleSelectCategory: (category: FlattedCategory) => void;
    handleDeleteGraphic: (graphic: FlattedCategory) => void;
    handleRemoveGraphic: (graphic: FlattedCategory) => void;
    selectedImages?: Set<string>;
    onToggleSelect?: (graphic: FlattedCategory, shiftKey: boolean) => void;
}

const MediaListComponent: React.FC<MediaListComponentProps> = (
    {
        mediaList,
        handleSelectCategory,
        handleDeleteGraphic,
        handleRemoveGraphic,
        selectedImages,
        onToggleSelect,
    }
) => {
    // Use a ref to store the previously loaded media items
    const prevMediaRef = useRef<Map<string, FlattedCategory>>(new Map());

    // Update the ref when mediaList changes
    mediaList.forEach((media) => {
        if (!prevMediaRef.current.has(media.id)) {
            prevMediaRef.current.set(media.id, media);
        }
    });

    // Clean up ref when items are removed from mediaList
    [...prevMediaRef.current.keys()].forEach((id) => {
        if (!mediaList.some((media) => media.id === id)) {
            prevMediaRef.current.delete(id);
        }
    });

    return (
        <>
            {mediaList.map((media) => {
                if (media.type === 'category') {
                    return (
                        <CategoryButton
                            key={media.id}
                            onClick={() => handleSelectCategory(media)}
                            category={media}
                        />
                    );
                } else if (media.type === 'image') {
                    // Retrieve from ref if exists to prevent re-rendering/loading
                    const cachedMedia = prevMediaRef.current.get(media.id) || media;
                    return (
                        <ImageButton
                            key={media.id}
                            onClick={() => handleDeleteGraphic(cachedMedia)}
                            graphic={cachedMedia as FlattedCategory}
                            isSelected={selectedImages?.has(media.id)}
                            onToggleSelect={onToggleSelect}
                        />
                    );
                } else {
                    return (
                        <ImageUploadButton
                            key={media.id}
                            callBack={() => handleRemoveGraphic(media)}
                            graphic={media}
                        />
                    );
                }
            })}
        </>
    );
};

export default memo(MediaListComponent);
